import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { StyleSection } from '@/components/summaryComponents/StyleSection';
import { AppliancesSection } from '@/components/summaryComponents/AppliancesSection';
import { LayoutSection } from '@/components/summaryComponents/LayoutSection';
import { FilesSection } from '@/components/summaryComponents/FilesSection';
import { AdditionalInfoSection } from '@/components/summaryComponents/AdditionalInfoSection';
import { ProjectService } from '@/services/ProjectService';

const ProjectRow = ({ project, isSelected, onSelect, onViewDetails }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [detailedProject, setDetailedProject] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const totalFlows = 5;
    const progress = (project.completedFlows?.length / totalFlows) * 100;
    const isSubmitted = project.status === 'submitted';

    useEffect(() => {
        const fetchDetailedProject = async () => {
            if (isExpanded && !detailedProject) {
                setIsLoading(true);
                try {
                    const fullProject = await ProjectService.getProjectById(project.id);
                    setDetailedProject(fullProject);
                } catch (error) {
                    console.error('Error fetching project details:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchDetailedProject();
    }, [isExpanded, project.id]);

    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const handleEditSection = path => {
        onViewDetails(`${project.id}/${path}`);
    };

    return (
        <Card className="mb-2" id={`project-${project.id}`}>
            <Collapsible open={isExpanded}>
                <CardHeader className="py-3">
                    <div className="flex items-center gap-4">
                        <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => onSelect(project.id)}
                            disabled={isSubmitted}
                        />
                        <CollapsibleTrigger
                            className="flex-1 flex items-center justify-between hover:bg-gray-50 p-2 rounded"
                            onClick={handleToggleExpand}
                        >
                            <div className="flex items-center gap-2">
                                {isExpanded ? (
                                    <ChevronDown className="h-5 w-5" />
                                ) : (
                                    <ChevronRight className="h-5 w-5" />
                                )}
                                <div className="flex items-center gap-2">
                                    <CardTitle className="text-lg">{project.name || 'Untitled Project'}</CardTitle>
                                    {isSubmitted && (
                                        <span className="text-sm px-2 py-1 bg-green-400 text-gray-700 rounded">
                                            Submitted
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 transition-all"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <div className="text-sm text-gray-500">{progress}%</div>
                            </div>
                        </CollapsibleTrigger>
                    </div>
                </CardHeader>

                <CollapsibleContent>
                    <CardContent className="pt-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                            </div>
                        ) : detailedProject ? (
                            <div className="space-y-4">
                                <StyleSection
                                    projectData={detailedProject}
                                    onEdit={handleEditSection}
                                    isEditable={!isSubmitted}
                                />
                                <AppliancesSection
                                    projectData={detailedProject}
                                    onEdit={handleEditSection}
                                    isEditable={!isSubmitted}
                                />
                                <LayoutSection
                                    projectData={detailedProject}
                                    onEdit={handleEditSection}
                                    isEditable={!isSubmitted}
                                />
                                <FilesSection
                                    projectData={detailedProject}
                                    onEdit={handleEditSection}
                                    isEditable={!isSubmitted}
                                />
                                <AdditionalInfoSection
                                    projectData={detailedProject}
                                    onEdit={handleEditSection}
                                    isEditable={!isSubmitted}
                                />

                                {!isSubmitted && (
                                    <Button
                                        variant="outline"
                                        onClick={() => onViewDetails(project.id)}
                                        className="w-full"
                                    >
                                        View Full Details
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="py-4 text-center text-gray-500">
                                Error loading project details. Please try again later.
                            </div>
                        )}
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
};

export default ProjectRow;
